"use client";
import { Anchor, Box, Button, Group, Stack, Text } from "@mantine/core";
import { usePathname, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";

import { setLocaleCookie } from "@/app/actions/set-local";
import i18nConfig from "@/i18n-config";

import { Logo } from "../logo/logo";
import styles from "./footer.module.css";

const creditLinks = ["Sound effects by ZapSplat.", "Background music by LofiVision."];

const languages = [
    { code: "en", label: "en" },
    { code: "de", label: "de" },
];

const CurlyTopBorder = () => (
    <svg className={styles.topBorder} viewBox="0 0 2880 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path
            className={styles.wavePath}
            d="M0 35
               Q 180 20, 360 30
               Q 540 40, 720 25
               Q 900 10, 1080 28
               Q 1260 45, 1440 30
               Q 1620 20, 1800 30
               Q 1980 40, 2160 25
               Q 2340 10, 2520 28
               Q 2700 45, 2880 30
               L 2880 60 L 0 60 Z"
            fill="currentColor"
        />
    </svg>
);

export const Footer = () => {
    const currentYear = new Date().getFullYear();
    const { i18n, t } = useTranslation();
    const currentLocale = i18n.language;
    const pathname = usePathname();
    const router = useRouter();

    const navLinks = [
        { href: "/", label: t("navbar.home") },
        { href: "/roadmap", label: t("navbar.roadmap") },
        { href: "/about", label: t("navbar.about") },
    ];

    const legalLinks = [
        { label: t("footer.imprint"), href: "impressum" },
        { label: t("footer.privacy"), href: "/datenschutz" },
        { label: t("footer.cancellation"), href: "/widerruf" },
        { label: t("footer.contact"), href: "mailto:christian@busshoff.net" },
    ];

    const changeLanguage = async (newLocale: string) => {
        if (!newLocale || newLocale === currentLocale) {
            return;
        }

        await setLocaleCookie(newLocale);

        if (currentLocale === i18nConfig.defaultLocale && !i18nConfig.prefixDefault) {
            router.push(`/${newLocale}${pathname}`);
        } else {
            router.push(pathname.replace(`/${currentLocale}`, `/${newLocale}`));
        }

        router.refresh();
    };

    return (
        <footer className={styles.footer}>
            {/* Curly decorative top border */}
            <CurlyTopBorder />

            <div className={styles.container}>
                {/* Main footer content */}
                <Group gap={0} align="flex-start" className={styles.content}>
                    {/* Logo */}
                    <Box className={styles.logoSection}>
                        <Logo />
                    </Box>

                    {/* Columns container */}
                    <Group gap={60} align="flex-start" className={styles.columns}>
                        {/* Navigation */}
                        <Stack gap="sm">
                            <Text className={styles.sectionTitle}>Navigation</Text>
                            {navLinks.map((link) => (
                                <Anchor key={link.href} href={link.href} className={styles.link} unstyled>
                                    {link.label}
                                </Anchor>
                            ))}
                        </Stack>

                        {/* Legal */}
                        <Stack gap="sm">
                            <Text className={styles.sectionTitle}>Legal</Text>
                            {legalLinks.map((link) => (
                                <Anchor key={link.href} href={link.href} className={styles.link} unstyled>
                                    {link.label}
                                </Anchor>
                            ))}
                        </Stack>

                        {/* Credits */}
                        <Stack gap="sm">
                            <Text className={styles.sectionTitle}>Credits</Text>
                            {creditLinks.map((credit) => (
                                <Text key={credit} className={styles.creditText} unstyled>
                                    {credit}
                                </Text>
                            ))}
                        </Stack>
                    </Group>
                </Group>

                {/* Bottom bar */}
                <Group justify="space-between" align="center" className={styles.bottomBar}>
                    <Group gap="md" align="center">
                        <Text className={styles.copyright}>&copy; {currentYear} Online Escape Room</Text>
                    </Group>

                    {/* Language Switcher */}
                    <Group gap={4} className={styles.languageSwitcher}>
                        {languages.map((lang) => (
                            <Button
                                key={lang.code}
                                onClick={() => changeLanguage(lang.code)}
                                className={`${styles.langButton} ${currentLocale === lang.code ? styles.langButtonActive : ""}`}
                                unstyled>
                                {lang.label}
                            </Button>
                        ))}
                    </Group>
                </Group>
            </div>
        </footer>
    );
};
